import PropTypes from 'prop-types';
import React, { Component } from 'react';
import b from 'b_';
import { translate } from 'react-i18next';
import { observer, inject } from 'mobx-react';
import BrowserStore from '../../browser/stores/BrowserStore';
import { ResearchStoreProp } from '../../stores/props';
import FuseInfoTooltip from '../FuseControls/FuseTooltip/FuseInfoTooltip';
import Icon from '../Icon/Icon';
import FuseBrowserModal from '../BrowserModal/FuseBrowserModal';
import { ObservableTextfield } from '../Textfield';
import LocalStorage, { LS_KEY } from '../../LocalStorage';
import { FTBodySm, FTHeading } from '../Fuse';
import FuseButton from '../FuseControls/FuseButton';
import FuseSwitch from '../FuseControls/FuseSwitch';
import FuseRadioButton from '../FuseControls/FuseRadioButton';
import Card from '../Fuse/Card';
import OldBrowserSettingsModal from './OldBrowserSettingsModal';
import { isFuse5Enabled } from '../../fuseFeatureFlag';
import SnackStore from '../../stores/SnackStore';

import './BrowserSettingsModal.css';

const block = b.lock('NewBrowserSettingsModal');

const AD_BLOCKER_OPTIONS = {
    ADS_BLOCKED: true,
    ADS_ALLOWED: false,
};

const AltCard = ({ titleIcon, title, subTitle, children }) => <Card>
    <div className={block('Title')}>
        <Icon type={titleIcon} className={block('Icon')} />
        <div className={block('TitleContainer')}>
            <FTHeading>{title}</FTHeading>
            {subTitle ? <span className={block('Subtitle')}>&nbsp;({subTitle})</span> : null}
        </div>
    </div>
    {children}
</Card>;

AltCard.propTypes = {
    titleIcon: Icon.propTypes.type,
    title: PropTypes.node,
    subTitle: PropTypes.node,
};

class FuseBrowserSettingsModal extends Component {
    static propTypes = {
        t: PropTypes.func.isRequired,
        browser: BrowserStore.PropType.isRequired,
        research: ResearchStoreProp,
        snack: SnackStore.PropType.isRequired,
        onRefresh: PropTypes.func,
    };

    save = () => {
        const { t, browser, research, snack, onRefresh } = this.props;

        browser.settingsModal.close();
        research.researchMode.commit();
        browser.topBarUnpinned.commit();
        browser.topBarNeedsMinimization.setValue(!browser.topBarUnpinned.getValue());

        // Handle AdBlock change if staged value differs from current value
        if (browser.stagedAdBlock.getValue() !== browser.adBlock.getValue()) {
            const newValue = browser.stagedAdBlock.getValue();
            browser.adBlock.setEnabled(newValue);
            browser.stagedAdBlock.commit();

            if (!browser.isHomePage && onRefresh) {
                onRefresh();
            }

            // Show the same snack messages as AdBlockerModal
            const optName = newValue === AD_BLOCKER_OPTIONS.ADS_BLOCKED ? 'ads_blocked' : 'ads_allowed';
            snack.showSnack({ type: 'success', content: t(`AdBlockerModal.snackAfter.${optName}`) });
        }
    };

    onAccessCodeSubmit = (event) => {
        event.preventDefault();
        const { t, browser } = this.props;
        const { accessCodeSettingsStore } = browser;
        accessCodeSettingsStore.inputErrorMsg.setValue('');
        accessCodeSettingsStore.onAccessCodeSubmit()
            .then(() => {
                accessCodeSettingsStore.accessCodeInput.setValue('');
                browser.isAccessCodeApplyMode.setTrue();
                accessCodeSettingsStore.apiDestroy().then(() => {
                    // need to show snack after navigating to home page
                    LocalStorage.set(LS_KEY.isAccessCodeApplyMode, true);
                    window.top.location.href = '/browser';
                });
            })
            .catch((e) => {
                const msg = t(`settingsModal.accessCodes.error_messages.${e.message}`);
                accessCodeSettingsStore.inputErrorMsg.setValue(msg);
            });
    };

    reset = () => {
        const { browser, research } = this.props;

        browser.topBarUnpinned.reset();
        research.researchMode.reset();
        browser.stagedAdBlock.reset();
    }

    closeAndReset = () => {
        this.props.browser.settingsModal.close();
        this.reset();
    };

    handleBack = () => {
        this.closeAndReset();
        this.props.browser.topBarMenu.open();
    };

    render() {
        const { t, browser, research } = this.props;
        const { accessCodeSettingsStore } = browser;

        return <FuseBrowserModal
            isOpen={browser.settingsModal.isOpen}
            small
            onClose={this.closeAndReset}
            onBack={this.handleBack}
            title={t('settingsModal.title')}
            closeTitle={t('settingsModal.closeTitle')}
            shouldReturnFocusAfterClose={false}
            footerActions={<>
                <FuseButton
                    type={FuseButton.type.SECONDARY}
                    color={FuseButton.color.BLUE}
                    onClick={this.closeAndReset}
                >{t('settingsModal.closeButton')}</FuseButton>
                <FuseButton
                    type={FuseButton.type.PRIMARY}
                    color={FuseButton.color.BLUE}
                    disabled={
                        !research.researchMode.isDirty() &&
                        !browser.topBarUnpinned.isDirty() &&
                        !browser.stagedAdBlock.hasChanged()
                    }
                    onClick={this.save}
                >{t('settingsModal.saveButton')}</FuseButton>
            </>}
        >
            <AltCard
                titleIcon="block"
                title={t('settingsModal.adBlocker.title')}
            >
                <FTBodySm className={block('Info')}>{t('settingsModal.adBlocker.info')}</FTBodySm>
                <FuseRadioButton
                    value={AD_BLOCKER_OPTIONS.ADS_ALLOWED}
                    store={browser.stagedAdBlock}
                    label={<FTBodySm>{t('settingsModal.adBlocker.allow_label')}</FTBodySm>}
                />
                <FuseRadioButton
                    value={AD_BLOCKER_OPTIONS.ADS_BLOCKED}
                    store={browser.stagedAdBlock}
                    label={<FTBodySm>{t('settingsModal.adBlocker.block_label')}</FTBodySm>}
                />
            </AltCard>
            { !browser.isFloatingIconEnabled() ? <AltCard
                titleIcon="web"
                title={t('settingsModal.topBar.title')}
            >
                <FTBodySm className={block('Info')}>{t('settingsModal.topBar.info')}</FTBodySm>
                <FuseSwitch
                    inverted
                    title={t('settingsModal.topBar.title')}
                    aria-label={t('settingsModal.topBar.title')}
                    label={!browser.topBarUnpinned.getDirtyValue() ? 'Enabled' : 'Disabled'}
                    store={browser.topBarUnpinned}
                />
            </AltCard> : null}
            {browser.settingsResearchMode ? <AltCard
                titleIcon="work_rounded"
                title={t('settingsModal.research.title')}
            >
                <FTBodySm className={block('Info')}>{t('settingsModal.research.info')}</FTBodySm>
                <FuseSwitch
                    title={t('settingsModal.research.title')}
                    aria-label={t('settingsModal.research.title')}
                    label={research.researchMode.getDirtyValue() ? 'Enabled' : 'Disabled'}
                    store={research.researchMode}
                />
            </AltCard> : null}
            {browser.allowAccessCodes ? <AltCard
                titleIcon="key_vpn_rounded"
                title={t('settingsModal.accessCodes.title')}
                subTitle={t('settingsModal.accessCodes.subtitle')}

            >
                <FTBodySm className={block('Info')}>
                    {t('settingsModal.accessCodes.info')}
                    <FuseInfoTooltip inline position={'bottom'}>
                        {t('settingsModal.accessCodes.toolTip')}
                    </FuseInfoTooltip>
                </FTBodySm>
                <form
                    noValidate
                    className={block('AccessCodeContainer')}
                    onSubmit={this.onAccessCodeSubmit}
                >
                    <ObservableTextfield
                        label={t('settingsModal.accessCodes.label')}
                        className={block('AccessCodeInput')}
                        store={accessCodeSettingsStore.accessCodeInput}
                        errorLabel={accessCodeSettingsStore.inputErrorMsg.value}
                    />
                    <FuseButton
                        type={FuseButton.type.PRIMARY}
                        color={FuseButton.color.BLUE}
                        disabled={!accessCodeSettingsStore.accessCodeInput.value}
                        loading={accessCodeSettingsStore.applyStatus.isLoading}
                        onClick={this.onAccessCodeSubmit}
                    >{t('settingsModal.accessCodes.applyButton')}</FuseButton>

                </form>
            </AltCard> : null}
            <AltCard
                titleIcon={browser.useRedesignedPolicy ? 'account_circle_rounded' : 'policy_rounded'}
                title={t(browser.useRedesignedPolicy ? 'settingsModal.activeRole' : 'settingsModal.activePolicy')}
            >
                <div className={block('ActivePolicy')}>
                    {browser.useRedesignedPolicy ? browser.roleName : browser.activePolicy}
                </div>
            </AltCard>
        </FuseBrowserModal>;
    }
}

const BrowserSettingsModal = (props) => {
    const ObservableBrowserSettingsModal = translate()(inject('browser', 'research', 'snack')(
        observer(FuseBrowserSettingsModal)
    ));
    return isFuse5Enabled() ? <ObservableBrowserSettingsModal {...props} /> : <OldBrowserSettingsModal />;
};

export default BrowserSettingsModal;
